import { GameAudio } from "./Audio";
import {
  createCrewCode,
  fetchLeaderboards,
  issueRankedRun,
  readClientId,
  submitRankedRun,
  type LeaderboardEntry,
  type Leaderboards,
  type PendingRankedSubmission,
  type RankedRun,
} from "./api";
import { calculateThrowVelocity, clamp, crossedRim, reflectVelocity, type MotionSample } from "./physics";
import {
  BASE_RIM,
  ENVIRONMENT_SEED_XOR,
  FIXED_STEP,
  GAMEPLAY_SEED_XOR,
  GRAVITY,
  GROUND_Y,
  HEIGHT,
  MAX_ATHLETE_COLLISIONS,
  MAX_RANKED_THROWS,
  MAX_THROW_SPEED,
  ROUND_TICKS,
  WALK_ZONE_MAX_X,
  WALK_ZONE_MIN_X,
  WIDTH,
  athleteSeed,
  clampToLegalReleasePoint,
  isLegalReleasePoint,
  parseRankedTrace,
  quantizeLegalReleasePoint,
  quantizeRankedThrow,
  rankedRimXAtTick,
  simulateRankedTrace,
  type RankedThrow,
} from "./rankedSimulation";
import {
  ATHLETE_PROFILES,
  MODES,
  ROUND_SECONDS,
  RULES_VERSION,
  SeededRandom,
  TIERS,
  parseCareerSave,
  pointsForDistance,
  profileById,
  randomSeed,
  tierIndexForXp,
  utcDay,
  type AthleteProfile,
  type CareerSave,
  type GameMode,
  type GameModeId,
} from "./rules";
import { readStoredNumber, readStoredValue, removeStoredValue, writeStoredValue } from "./storage";

type GameState = "menu" | "countdown" | "playing" | "paused" | "results";
type HairStyle = "fade" | "curls" | "braids" | "high-top" | "buzz";

interface Athlete {
  id: number;
  x: number;
  y: number;
  previousX: number;
  previousY: number;
  velocityX: number;
  velocityY: number;
  direction: -1 | 1;
  airborne: boolean;
  held: boolean;
  touched: boolean;
  scored: boolean;
  rotation: number;
  angularVelocity: number;
  gait: number;
  turnTimer: number;
  respawnTimer: number;
  collisions: number;
  throwPoints: number;
  throwOriginTick: number;
  throwOriginX: number;
  skin: string;
  skinShadow: string;
  jersey: string;
  jerseyShadow: string;
  number: number;
  hair: HairStyle;
  blinkTimer: number;
}

interface Particle {
  x: number;
  y: number;
  velocityX: number;
  velocityY: number;
  life: number;
  maximumLife: number;
  color: string;
  radius: number;
}

interface FloatingText {
  x: number;
  y: number;
  text: string;
  color: string;
  life: number;
}

export interface GameElements {
  shell: HTMLElement;
  canvas: HTMLCanvasElement;
  overlay: HTMLElement;
  score: HTMLElement;
  time: HTMLElement;
  best: HTMLElement;
  streak: HTMLElement;
  mode: HTMLElement;
  wind: HTMLElement;
  pauseButton: HTMLButtonElement;
  soundButton: HTMLButtonElement;
  fullscreenButton: HTMLButtonElement;
}

const SKINS = [
  ["#6f3d20", "#472412"],
  ["#81502d", "#563019"],
  ["#955b32", "#64371d"],
  ["#a76a3b", "#704322"],
  ["#bd8050", "#81532f"],
] as const;
const JERSEYS = [
  ["#e55a3c", "#9d2d25"],
  ["#e5b83f", "#a56c18"],
  ["#43a3d9", "#21668f"],
  ["#7e66d5", "#4b3796"],
  ["#46b875", "#247347"],
  ["#ef7fb3", "#a83f73"],
] as const;
const HAIR: readonly HairStyle[] = ["fade", "curls", "braids", "high-top", "buzz"];
const CAREER_KEY = "hoop-dreams-career";
const PENDING_SUBMISSION_KEY = "hoop-dreams-pending-submission";

export class HoopDreamsGame {
  private readonly elements: GameElements;
  private readonly context: CanvasRenderingContext2D;
  private readonly audio: GameAudio;
  private readonly athletes: Athlete[];
  private readonly particles: Particle[] = [];
  private readonly floatingTexts: FloatingText[] = [];
  private readonly pointerSamples: MotionSample[] = [];
  private readonly rankedTrace: RankedThrow[] = [];
  private readonly clientId: string;
  private state: GameState = "menu";
  private previousState: GameState = "menu";
  private grabbed: Athlete | null = null;
  private activePointerId: number | null = null;
  private pointerX = 0;
  private pointerY = 0;
  private score = 0;
  private streak = 0;
  private best = 0;
  private bestBeforeRound = 0;
  private timeRemaining = ROUND_SECONDS;
  private countdownRemaining = 0;
  private countdownBeat = 4;
  private countdownEndsAt = 0;
  private lastFrame = performance.now();
  private accumulator = 0;
  private animationFrame = 0;
  private shake = 0;
  private soundEnabled: boolean;
  private career: CareerSave;
  private selectedMode: GameModeId;
  private currentMode: GameMode;
  private activeProfile: AthleteProfile;
  private gameplayRandom = new SeededRandom(randomSeed());
  private athleteRandoms = Array.from({ length: 7 }, () => new SeededRandom(randomSeed()));
  private environmentRandom = new SeededRandom(randomSeed());
  private visualRandom = new SeededRandom(randomSeed());
  private rankedRun: RankedRun | null = null;
  private pendingSubmission: PendingRankedSubmission | null = null;
  private leaderboards: Leaderboards | null = null;
  private leaderboardStatus = "";
  private submissionStatus = "";
  private starting = false;
  private leaderboardRequest = 0;
  private roundTick = 0;
  private rimX: number = BASE_RIM.x;
  private previousRimX: number = BASE_RIM.x;
  private rimPhase = 0;
  private wind = 0;
  private windTarget = 0;
  private windTimer = 0;
  private crowdEnergy = 0.15;
  private menuExpanded = false;

  constructor(elements: GameElements) {
    this.elements = elements;
    const context = elements.canvas.getContext("2d");
    if (!context) throw new Error("Canvas 2D rendering is unavailable");
    this.context = context;
    this.elements.canvas.width = WIDTH;
    this.elements.canvas.height = HEIGHT;
    this.soundEnabled = readStoredValue("hoop-dreams-sound") !== "off";
    this.audio = new GameAudio(this.soundEnabled);
    this.clientId = readClientId();
    this.career = parseCareerSave(readStoredValue(CAREER_KEY), readStoredNumber("hoop-dreams-best"));
    this.pendingSubmission = parsePendingSubmission(readStoredValue(PENDING_SUBMISSION_KEY));
    this.selectedMode = this.career.selectedMode;
    this.currentMode = MODES[this.selectedMode];
    this.activeProfile = this.resolveSelectedProfile();
    this.best = this.career.bests[this.selectedMode];
    this.athletes = Array.from({ length: 7 }, (_, index) => this.createAthlete(index));
    this.bindEvents();
    this.updateHud();
    this.renderOverlay();
    this.updateControls();
    if (this.currentMode.ranked) void this.refreshLeaderboards();
    this.animationFrame = requestAnimationFrame(this.loop);
    if (this.pendingSubmission) void this.submitPendingScore();
  }

  destroy(): void {
    cancelAnimationFrame(this.animationFrame);
    this.elements.canvas.removeEventListener("pointerdown", this.handlePointerDown);
    this.elements.canvas.removeEventListener("pointermove", this.handlePointerMove);
    this.elements.canvas.removeEventListener("pointerup", this.handlePointerUp);
    this.elements.canvas.removeEventListener("pointercancel", this.handlePointerCancel);
    this.elements.overlay.removeEventListener("click", this.handleOverlayClick);
    this.elements.overlay.removeEventListener("change", this.handleOverlayChange);
    window.removeEventListener("keydown", this.handleKeyDown);
  }

  private readonly loop = (now: number): void => {
    const rawElapsed = Math.max(0, (now - this.lastFrame) / 1_000);
    const rankedClock = this.currentMode.ranked && (this.state === "countdown" || this.state === "playing");
    const elapsed = rankedClock ? rawElapsed : Math.min(rawElapsed, 0.05);
    this.lastFrame = now;

    if (this.state === "playing") {
      this.timeRemaining = this.currentMode.ranked
        ? Math.max(0, ROUND_SECONDS - this.roundTick * FIXED_STEP)
        : Math.max(0, this.timeRemaining - elapsed);
      if (!this.currentMode.ranked && this.timeRemaining <= 0) this.finishRound();
    }

    this.accumulator = rankedClock
      ? this.accumulator + elapsed
      : Math.min(this.accumulator + elapsed, 0.2);
    while (this.accumulator >= FIXED_STEP) {
      this.update(FIXED_STEP);
      this.accumulator -= FIXED_STEP;
      if (this.state === "results") {
        this.accumulator = 0;
        break;
      }
    }

    this.draw(now / 1_000);
    this.animationFrame = requestAnimationFrame(this.loop);
  };

  private bindEvents(): void {
    this.elements.canvas.addEventListener("pointerdown", this.handlePointerDown);
    this.elements.canvas.addEventListener("pointermove", this.handlePointerMove);
    this.elements.canvas.addEventListener("pointerup", this.handlePointerUp);
    this.elements.canvas.addEventListener("pointercancel", this.handlePointerCancel);
    this.elements.canvas.addEventListener("contextmenu", (event) => event.preventDefault());
    this.elements.overlay.addEventListener("click", this.handleOverlayClick);
    this.elements.overlay.addEventListener("change", this.handleOverlayChange);
    this.elements.pauseButton.addEventListener("click", () => this.togglePause());
    this.elements.soundButton.addEventListener("click", () => this.toggleSound());
    this.elements.fullscreenButton.addEventListener("click", () => void this.toggleFullscreen());
    window.addEventListener("keydown", this.handleKeyDown);
    document.addEventListener("visibilitychange", () => {
      if (document.hidden && this.state === "playing") this.pause();
    });
  }

  private readonly handleOverlayClick = (event: MouseEvent): void => {
    const target = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-action]");
    const action = target?.dataset.action;
    if (!action) return;

    void this.audio.unlock();
    if (action === "quick-start") {
      this.selectMode("career");
      void this.startRound();
    }
    if (action === "start" || action === "restart") void this.startRound();
    if (action === "resume") this.resume();
    if (action === "menu") this.openMenu();
    if (action === "modes") {
      this.menuExpanded = true;
      this.renderOverlay();
    }
    if (action === "simple-menu") {
      this.menuExpanded = false;
      this.renderOverlay();
    }
    if (action === "sound") this.toggleSound();
    if (action === "fullscreen") void this.toggleFullscreen();
    if (action === "new-crew") this.setCrewCode(createCrewCode());
    if (action === "copy-crew") void navigator.clipboard?.writeText(this.career.crewCode);
    if (action === "retry-submission") void this.submitPendingScore();

    const mode = target?.dataset.mode as GameModeId | undefined;
    if (mode && mode in MODES) this.selectMode(mode);
    const profileId = target?.dataset.profile;
    if (profileId) this.selectProfile(profileId);
  };

  private readonly handleOverlayChange = (event: Event): void => {
    if (this.starting || this.state !== "menu") return;
    const input = event.target as HTMLInputElement;
    if (input.dataset.field === "player-name") {
      this.career.playerName = input.value.replace(/[^\p{L}\p{N} _.-]/gu, "").trim().slice(0, 18) || "Rookie";
      this.saveCareer();
      this.renderOverlay();
    }
    if (input.dataset.field === "crew-code") this.setCrewCode(input.value);
  };

  private readonly handleKeyDown = (event: KeyboardEvent): void => {
    if (event.repeat) return;
    if (event.key === "Escape" || event.key.toLowerCase() === "p") this.togglePause();
    if (event.key.toLowerCase() === "m") this.toggleSound();
    if (event.key === "Enter" && (this.state === "menu" || this.state === "results")) {
      void this.audio.unlock();
      if (this.state === "menu" && !this.menuExpanded) this.selectMode("career");
      void this.startRound();
    }
  };

  private readonly handlePointerDown = (event: PointerEvent): void => {
    if (
      this.state !== "playing" || this.activePointerId !== null ||
      (this.currentMode.ranked && this.rankedTrace.length >= MAX_RANKED_THROWS)
    ) return;
    event.preventDefault();
    void this.audio.unlock();
    const point = this.toWorldPoint(event);
    if (point.x < WALK_ZONE_MIN_X || point.x > WALK_ZONE_MAX_X) return;
    this.pointerX = point.x;
    this.pointerY = point.y;

    const athlete = this.athletes
      .filter((candidate) => !candidate.touched && candidate.respawnTimer <= 0 && !candidate.airborne)
      .map((candidate) => ({ candidate, distance: Math.hypot(candidate.x - point.x, candidate.y - point.y) }))
      .filter(({ distance }) => distance < 70 * this.activeProfile.size)
      .sort((left, right) => left.distance - right.distance)[0]?.candidate;

    if (!athlete) return;
    this.grabbed = athlete;
    this.activePointerId = event.pointerId;
    athlete.held = true;
    athlete.touched = true;
    athlete.airborne = true;
    athlete.velocityX = 0;
    athlete.velocityY = 0;
    athlete.angularVelocity = 0;
    athlete.collisions = 0;
    athlete.throwOriginTick = this.roundTick;
    athlete.throwOriginX = Math.round(athlete.x * 100) / 100;
    athlete.throwPoints = pointsForDistance(athlete.throwOriginX, this.rimX);
    this.pointerSamples.length = 0;
    this.recordPointer(point.x, point.y, event.timeStamp);
    this.elements.canvas.setPointerCapture(event.pointerId);
    this.audio.grab();
  };

  private readonly handlePointerMove = (event: PointerEvent): void => {
    if (event.pointerId !== this.activePointerId) return;
    const point = this.toWorldPoint(event);
    if (this.grabbed && !this.recordLegalPointerPath(point.x, point.y, event.timeStamp)) {
      this.releaseGrabbed(true);
      return;
    }
  };

  private readonly handlePointerUp = (event: PointerEvent): void => {
    if (!this.grabbed || event.pointerId !== this.activePointerId) return;
    const point = this.toWorldPoint(event);
    this.recordLegalPointerPath(point.x, point.y, event.timeStamp);
    this.releaseGrabbed(true);
  };

  private readonly handlePointerCancel = (event: PointerEvent): void => {
    if (event.pointerId === this.activePointerId) this.releaseGrabbed(this.state === "playing");
  };

  private releaseGrabbed(launch: boolean): void {
    const athlete = this.grabbed;
    if (!athlete) return;

    athlete.held = false;
    if (launch) {
      const releasePoint = quantizeLegalReleasePoint(this.pointerX, this.pointerY, this.rimX);
      athlete.x = releasePoint.x;
      athlete.y = releasePoint.y;
      const velocity = calculateThrowVelocity(this.pointerSamples, MAX_THROW_SPEED);
      athlete.velocityX = velocity.x * this.activeProfile.throwPower;
      athlete.velocityY = velocity.y * this.activeProfile.throwPower;
      if (this.currentMode.ranked && this.rankedRun && this.roundTick < ROUND_TICKS) {
        const rankedThrow = quantizeRankedThrow(
          this.roundTick + 1,
          athlete.id,
          athlete.x,
          athlete.y,
          athlete.velocityX,
          athlete.velocityY,
          athlete.throwOriginTick,
          athlete.throwOriginX,
        );
        this.rankedTrace.push(rankedThrow);
        [, , athlete.x, athlete.y, athlete.velocityX, athlete.velocityY] = rankedThrow;
      }
      athlete.angularVelocity = clamp(athlete.velocityX / 190, -7, 7);
      const speed = Math.hypot(athlete.velocityX, athlete.velocityY);
      this.audio.launch(speed);
      this.audio.scream(speed, athlete.id);
    }

    const pointerId = this.activePointerId;
    if (pointerId !== null && this.elements.canvas.hasPointerCapture(pointerId)) {
      this.elements.canvas.releasePointerCapture(pointerId);
    }
    this.grabbed = null;
    this.activePointerId = null;
    this.pointerSamples.length = 0;
  }

  private recordLegalPointerPath(x: number, y: number, time: number): boolean {
    const startX = this.pointerX;
    const startY = this.pointerY;
    const distance = Math.hypot(x - startX, y - startY);
    const steps = Math.max(1, Math.ceil(distance / 24));
    let legalRatio = 0;
    for (let step = 1; step <= steps; step += 1) {
      const ratio = step / steps;
      const sampleX = startX + (x - startX) * ratio;
      const sampleY = startY + (y - startY) * ratio;
      if (!isLegalReleasePoint(sampleX, sampleY, this.rimX)) {
        let illegalRatio = ratio;
        for (let refinement = 0; refinement < 20; refinement += 1) {
          const midpoint = (legalRatio + illegalRatio) / 2;
          const midpointX = startX + (x - startX) * midpoint;
          const midpointY = startY + (y - startY) * midpoint;
          if (isLegalReleasePoint(midpointX, midpointY, this.rimX)) legalRatio = midpoint;
          else illegalRatio = midpoint;
        }
        const legalX = startX + (x - startX) * legalRatio;
        const legalY = startY + (y - startY) * legalRatio;
        this.pointerX = legalX;
        this.pointerY = legalY;
        if (this.grabbed) {
          this.grabbed.x = legalX;
          this.grabbed.y = legalY;
        }
        this.recordPointer(legalX, legalY, time);
        return false;
      }
      legalRatio = ratio;
    }
    this.pointerX = x;
    this.pointerY = y;
    this.recordPointer(x, y, time);
    return true;
  }

  private recordPointer(x: number, y: number, time: number): void {
    this.pointerSamples.push({ x, y, time });
    const cutoff = time - 180;
    while ((this.pointerSamples[0]?.time ?? time) < cutoff) this.pointerSamples.shift();
  }

  private update(delta: number): void {
    if (this.state === "paused" || this.state === "results") return;

    if (this.state === "countdown") {
      this.countdownRemaining = this.currentMode.ranked
        ? Math.max(0, (this.countdownEndsAt - performance.now()) / 1_000)
        : this.countdownRemaining - delta;
      const beat = Math.ceil(this.countdownRemaining);
      if (beat !== this.countdownBeat && beat > 0) {
        this.countdownBeat = beat;
        this.audio.countdown(false);
      }
      if (this.countdownRemaining <= 0) {
        this.audio.countdown(true);
        this.setState("playing");
      }
      this.updateHud();
      return;
    }

    this.roundTick += 1;
    this.updateEnvironment(delta);
    for (const athlete of this.athletes) this.updateAthlete(athlete, delta);
    this.updateEffects(delta);
    this.shake = Math.max(0, this.shake - delta * 18);
    this.crowdEnergy += (0.15 - this.crowdEnergy) * (1 - Math.exp(-delta * 1.5));
    this.updateHud();
    if (this.currentMode.ranked && this.roundTick >= ROUND_TICKS) this.finishRound();
  }

  private updateEnvironment(delta: number): void {
    this.previousRimX = this.rimX;
    this.rimX = this.currentMode.movingRim
      ? rankedRimXAtTick(this.roundTick, this.rimPhase)
      : BASE_RIM.x;

    this.windTimer -= delta;
    if (this.windTimer <= 0) {
      this.windTarget = (this.environmentRandom.next() * 2 - 1) * (this.currentMode.id === "career" ? 125 : 185);
      this.windTimer = 3.8 + this.environmentRandom.next() * 3.4;
    }
    this.wind += (this.windTarget - this.wind) * (1 - Math.exp(-delta * 0.85));
  }

  private updateAthlete(athlete: Athlete, delta: number): void {
    athlete.previousX = athlete.x;
    athlete.previousY = athlete.y;
    const athleteRandom = this.athleteRandoms[athlete.id] ?? this.gameplayRandom;
    athlete.blinkTimer -= delta;
    if (athlete.blinkTimer < -0.12) athlete.blinkTimer = 1.6 + this.visualRandom.next() * 3.5;

    if (athlete.respawnTimer > 0) {
      athlete.respawnTimer -= delta;
      if (athlete.respawnTimer <= 0) this.resetAthlete(athlete);
      return;
    }

    if (athlete.held) {
      if (!isLegalReleasePoint(this.pointerX, this.pointerY, this.rimX)) {
        const legalPoint = clampToLegalReleasePoint(this.pointerX, this.pointerY, this.rimX);
        this.pointerX = legalPoint.x;
        this.pointerY = legalPoint.y;
        athlete.x = legalPoint.x;
        athlete.y = legalPoint.y;
        this.recordPointer(legalPoint.x, legalPoint.y, performance.now());
        this.releaseGrabbed(true);
        return;
      }
      const follow = 1 - Math.exp(-delta * 22);
      athlete.x += (this.pointerX - athlete.x) * follow;
      athlete.y += (this.pointerY - athlete.y) * follow;
      athlete.rotation += (Math.atan2(this.pointerY - athlete.y, this.pointerX - athlete.x) * 0.1 - athlete.rotation) * follow;
      return;
    }

    if (athlete.airborne) {
      athlete.velocityY += GRAVITY * this.activeProfile.gravity * delta;
      athlete.velocityX += this.wind * delta;
      athlete.velocityX *= Math.pow(0.998, delta * 120);
      athlete.x += athlete.velocityX * delta;
      athlete.y += athlete.velocityY * delta;
      athlete.rotation += athlete.angularVelocity * delta;

      const crossedPlane = athlete.previousY < BASE_RIM.y && athlete.y >= BASE_RIM.y && athlete.velocityY > 0;
      const relativePreviousX = athlete.previousX - this.previousRimX;
      const relativeX = athlete.x - this.rimX;
      if (this.state === "playing" && !athlete.scored && crossedRim({
        previousX: relativePreviousX,
        previousY: athlete.previousY,
        x: relativeX,
        y: athlete.y,
        velocityY: athlete.velocityY,
        rimX: 0,
        rimY: BASE_RIM.y,
        innerWidth: BASE_RIM.innerWidth,
      })) {
        this.scoreBasket(athlete);
        return;
      }
      if (this.state === "playing" && crossedPlane) this.checkNearMiss(athlete);

      if (this.resolveRimCollision(athlete, this.rimX - BASE_RIM.halfWidth, BASE_RIM.y)) return;
      if (this.resolveRimCollision(athlete, this.rimX + BASE_RIM.halfWidth, BASE_RIM.y)) return;
      this.resolveBackboardCollision(athlete);

      if (athlete.y >= GROUND_Y - 36 && athlete.velocityY > 0) {
        athlete.y = GROUND_Y - 36;
        if (this.recordCollision(athlete)) return;
        if (Math.abs(athlete.velocityY) > 260) {
          athlete.velocityY *= -0.28;
          athlete.velocityX *= 0.72;
          athlete.angularVelocity *= 0.65;
          this.spawnDust(athlete.x, GROUND_Y - 7, 4);
        } else if (athlete.touched) {
          this.streak = 0;
          this.queueRespawn(athlete, 0.18);
          return;
        } else {
          athlete.airborne = false;
          athlete.velocityY = 0;
          athlete.velocityX = athlete.direction * (38 + athleteRandom.next() * 38);
          athlete.rotation = 0;
          athlete.angularVelocity = 0;
          this.streak = 0;
        }
      }

      if (athlete.x < -120 || athlete.x > WIDTH + 120 || athlete.y > HEIGHT + 160) {
        this.streak = 0;
        this.resetAthlete(athlete);
      }
      return;
    }

    athlete.turnTimer -= delta;
    if (athlete.turnTimer <= 0) {
      athlete.turnTimer = 1.2 + athleteRandom.next() * 3.4;
      if (athleteRandom.next() < 0.35) athlete.direction = athlete.direction === 1 ? -1 : 1;
      athlete.velocityX = athlete.direction * (38 + athleteRandom.next() * 42);
    }
    athlete.x += athlete.velocityX * delta;
    athlete.gait += Math.abs(athlete.velocityX) * delta * 0.09;
    athlete.rotation *= Math.pow(0.001, delta);

    if (athlete.x < WALK_ZONE_MIN_X) {
      athlete.x = WALK_ZONE_MIN_X;
      athlete.direction = 1;
      athlete.velocityX = Math.abs(athlete.velocityX);
    }
    if (athlete.x > WALK_ZONE_MAX_X) {
      athlete.x = WALK_ZONE_MAX_X;
      athlete.direction = -1;
      athlete.velocityX = -Math.abs(athlete.velocityX);
    }
  }

  private queueRespawn(athlete: Athlete, delay: number): void {
    athlete.respawnTimer = delay;
    athlete.held = false;
    athlete.airborne = false;
    athlete.velocityX = 0;
    athlete.velocityY = 0;
    athlete.angularVelocity = 0;
  }

  private recordCollision(athlete: Athlete): boolean {
    athlete.collisions += 1;
    if (athlete.collisions < MAX_ATHLETE_COLLISIONS) return false;
    this.streak = 0;
    this.queueRespawn(athlete, 0.18);
    return true;
  }

  private checkNearMiss(athlete: Athlete): void {
    const ratio = (BASE_RIM.y - athlete.previousY) / Math.max(athlete.y - athlete.previousY, 0.001);
    const crossingX = athlete.previousX + (athlete.x - athlete.previousX) * ratio;
    const distance = Math.abs(crossingX - this.rimX);
    if (distance <= BASE_RIM.innerWidth / 2 || distance > BASE_RIM.innerWidth / 2 + 62) return;
    this.crowdEnergy = 0.85;
    this.audio.gasp();
    this.floatingTexts.push({ x: crossingX, y: BASE_RIM.y - 46, text: "SO CLOSE", color: "#ffd49a", life: 0.9 });
  }

  private resolveRimCollision(athlete: Athlete, x: number, y: number): boolean {
    const radius = 27 * this.activeProfile.size;
    const dx = athlete.x - x;
    const dy = athlete.y - y;
    const distance = Math.hypot(dx, dy);
    if (distance >= radius || distance === 0) return false;

    const normalX = dx / distance;
    const normalY = dy / distance;
    athlete.x = x + normalX * radius;
    athlete.y = y + normalY * radius;
    if (this.recordCollision(athlete)) return true;

    const reflected = reflectVelocity(
      { x: athlete.velocityX, y: athlete.velocityY },
      { x: normalX, y: normalY },
      0.58,
    );
    athlete.velocityX = reflected.x;
    athlete.velocityY = reflected.y;
    athlete.angularVelocity += normalX * 2.8;
    this.audio.rim();
    this.shake = Math.max(this.shake, 2.5);
    return false;
  }

  private resolveBackboardCollision(athlete: Athlete): void {
    const boardX = this.rimX + 97;
    if (
      athlete.previousX + 23 <= boardX &&
      athlete.x + 23 >= boardX &&
      athlete.y > 125 &&
      athlete.y < 279 &&
      athlete.velocityX > 0
    ) {
      athlete.x = boardX - 24;
      athlete.velocityX *= -0.55;
      athlete.angularVelocity *= -0.75;
      if (this.recordCollision(athlete)) return;
      this.audio.rim();
      this.shake = Math.max(this.shake, 3);
    }
  }

  private scoreBasket(athlete: Athlete): void {
    const perfect = Math.abs(athlete.x - this.rimX) < this.activeProfile.accuracy;
    const points = athlete.throwPoints;
    this.score += points;
    this.streak += 1;
    this.best = Math.max(this.best, this.score);
    athlete.scored = true;
    if (this.grabbed === athlete) this.grabbed = null;
    this.audio.score(perfect, this.streak);
    this.shake = perfect ? 7 : 4;
    this.crowdEnergy = 1;
    this.spawnScoreBurst(athlete.x, BASE_RIM.y, perfect);
    const label = perfect
      ? `PERFECT · ${points}× DISTANCE · +${points}`
      : this.streak >= 3
        ? `STREAK ${this.streak} · ${points}× DISTANCE · +${points}`
        : `${points}× DISTANCE · +${points}`;
    this.floatingTexts.push({
      x: athlete.x,
      y: BASE_RIM.y - 34,
      text: label,
      color: perfect ? "#ffe274" : "#ffffff",
      life: 1.25,
    });
  }

  private updateEffects(delta: number): void {
    for (const particle of this.particles) {
      particle.life -= delta;
      particle.velocityY += 540 * delta;
      particle.x += particle.velocityX * delta;
      particle.y += particle.velocityY * delta;
    }
    for (let index = this.particles.length - 1; index >= 0; index -= 1) {
      if ((this.particles[index]?.life ?? 0) <= 0) this.particles.splice(index, 1);
    }
    for (const text of this.floatingTexts) {
      text.life -= delta;
      text.y -= 42 * delta;
    }
    for (let index = this.floatingTexts.length - 1; index >= 0; index -= 1) {
      if ((this.floatingTexts[index]?.life ?? 0) <= 0) this.floatingTexts.splice(index, 1);
    }
  }

  private async startRound(): Promise<void> {
    if (this.starting || (this.state !== "menu" && this.state !== "results")) return;
    const modeId = this.selectedMode;
    const mode = MODES[modeId];
    if (modeId === "daily" && this.career.dailyCompleted === utcDay()) return;
    this.starting = true;
    this.submissionStatus = "";
    this.currentMode = mode;
    this.renderOverlay();

    this.rankedRun = null;
    let seed = randomSeed();
    if (mode.ranked) {
      try {
        this.rankedRun = await issueRankedRun(mode.id as "moving" | "daily", this.clientId, this.career.crewCode);
        seed = this.rankedRun.seed;
      } catch (error) {
        this.starting = false;
        this.submissionStatus = messageFrom(error);
        this.renderOverlay();
        return;
      }
    }

    this.activeProfile = mode.ranked ? profileById("rookie") : this.resolveSelectedProfile();
    this.gameplayRandom = new SeededRandom(seed ^ GAMEPLAY_SEED_XOR);
    this.athleteRandoms = Array.from({ length: 7 }, (_, athleteId) => new SeededRandom(athleteSeed(seed, athleteId)));
    this.environmentRandom = new SeededRandom(seed ^ ENVIRONMENT_SEED_XOR);
    this.visualRandom = new SeededRandom(seed ^ 0x85ebca6b);
    this.rimPhase = this.environmentRandom.next() * Math.PI * 2;
    this.wind = 0;
    this.windTarget = 0;
    this.windTimer = 0;
    this.rimX = mode.movingRim ? rankedRimXAtTick(0, this.rimPhase) : BASE_RIM.x;
    this.previousRimX = this.rimX;
    this.roundTick = 0;
    this.rankedTrace.length = 0;
    this.score = 0;
    this.streak = 0;
    this.best = this.career.bests[modeId];
    this.bestBeforeRound = this.best;
    this.timeRemaining = ROUND_SECONDS;
    this.countdownRemaining = 3.15;
    const startedAt = performance.now();
    this.countdownEndsAt = startedAt + 3_150;
    this.countdownBeat = 4;
    this.accumulator = 0;
    this.particles.length = 0;
    this.floatingTexts.length = 0;
    this.athletes.forEach((athlete) => this.resetAthlete(athlete));
    this.starting = false;
    this.setState("countdown");
  }

  private finishRound(): void {
    if (this.state === "results") return;
    this.releaseGrabbed(false);
    const run = this.rankedRun;
    if (run) {
      const verified = simulateRankedTrace(run.mode, run.seed, this.rankedTrace);
      if (!verified.valid) {
        this.submissionStatus = verified.error ?? "Ranked run could not be verified";
        this.setState("results");
        return;
      }
      this.score = verified.score;
    }

    const modeId = this.currentMode.id;
    this.career.bests[modeId] = Math.max(this.career.bests[modeId], this.score);
    this.best = this.career.bests[modeId];
    this.career.xp += Math.max(8, Math.floor(this.score * 4 * this.currentMode.xpMultiplier));
    if (run?.mode === "daily") this.career.dailyCompleted = run.day;
    this.saveCareer();

    if (run) {
      this.pendingSubmission = {
        run,
        playerName: this.career.playerName,
        trace: [...this.rankedTrace],
      };
      this.savePendingSubmission();
    }
    this.setState("results");
    if (this.pendingSubmission) void this.submitPendingScore();
  }

  private async submitPendingScore(): Promise<void> {
    const submission = this.pendingSubmission;
    if (!submission) return;
    if (submission.run.expiresAt <= Date.now()) {
      this.clearPendingSubmission();
      this.submissionStatus = "Ranked submission expired";
      this.renderOverlay();
      return;
    }

    this.submissionStatus = "Submitting verified run…";
    this.renderOverlay();
    try {
      const result = await submitRankedRun(submission, this.clientId);
      if (this.pendingSubmission?.run.id === submission.run.id) this.clearPendingSubmission();
      if (this.selectedMode === submission.run.mode && this.career.crewCode === submission.run.crewCode) {
        this.leaderboards = result;
      }
      this.submissionStatus = result.placement ? `Verified rank #${result.placement}` : "Verified score submitted";
    } catch (error) {
      this.submissionStatus = `${messageFrom(error)} · retry available`;
    }
    this.renderOverlay();
  }

  private pause(): void {
    if (this.currentMode.ranked) return;
    if (this.state !== "playing" && this.state !== "countdown") return;
    this.previousState = this.state;
    this.releaseGrabbed(false);
    this.setState("paused");
  }

  private resume(): void {
    if (this.state !== "paused") return;
    this.lastFrame = performance.now();
    this.accumulator = 0;
    this.setState(this.previousState === "countdown" ? "countdown" : "playing");
  }

  private togglePause(): void {
    if (this.state === "paused") this.resume();
    else this.pause();
  }

  private openMenu(): void {
    this.menuExpanded = false;
    this.score = 0;
    this.streak = 0;
    this.timeRemaining = ROUND_SECONDS;
    this.currentMode = MODES[this.selectedMode];
    this.activeProfile = this.resolveSelectedProfile();
    this.best = this.career.bests[this.selectedMode];
    this.athletes.forEach((athlete) => this.resetAthlete(athlete));
    this.setState("menu");
    if (this.currentMode.ranked) void this.refreshLeaderboards();
  }

  private selectMode(mode: GameModeId): void {
    if (this.starting || this.state !== "menu") return;
    this.selectedMode = mode;
    this.currentMode = MODES[mode];
    this.career.selectedMode = mode;
    this.best = this.career.bests[mode];
    this.saveCareer();
    this.leaderboards = null;
    this.leaderboardStatus = "";
    this.renderOverlay();
    if (this.currentMode.ranked) void this.refreshLeaderboards();
  }

  private selectProfile(profileId: string): void {
    if (this.starting || this.state !== "menu") return;
    const profile = profileById(profileId);
    if (profile.unlockTier > tierIndexForXp(this.career.xp)) return;
    this.career.selectedProfile = profile.id;
    this.activeProfile = profile;
    this.saveCareer();
    this.renderOverlay();
  }

  private setCrewCode(value: string): void {
    if (this.starting || this.state !== "menu") return;
    this.career.crewCode = value.toUpperCase().replace(/[^A-HJ-NP-Z2-9]/g, "").slice(0, 10);
    this.saveCareer();
    this.renderOverlay();
    if (this.currentMode.ranked) void this.refreshLeaderboards();
  }

  private async refreshLeaderboards(): Promise<void> {
    const requestId = ++this.leaderboardRequest;
    const mode = this.selectedMode;
    const crewCode = this.career.crewCode;
    this.leaderboardStatus = "Loading rankings…";
    if (this.state === "menu") this.renderOverlay();
    try {
      const leaderboards = await fetchLeaderboards(mode, crewCode);
      if (requestId !== this.leaderboardRequest || mode !== this.selectedMode || crewCode !== this.career.crewCode) return;
      this.leaderboards = leaderboards;
      this.leaderboardStatus = "";
    } catch {
      if (requestId !== this.leaderboardRequest) return;
      this.leaderboardStatus = "Rankings offline";
    }
    if (this.state === "menu") this.renderOverlay();
  }

  private setState(state: GameState): void {
    this.state = state;
    this.elements.shell.dataset.state = state;
    this.renderOverlay();
    this.updateControls();
  }

  private renderOverlay(): void {
    if (this.state === "playing" || this.state === "countdown") {
      this.elements.overlay.hidden = true;
      return;
    }

    this.elements.overlay.hidden = false;
    if (this.state === "menu") {
      this.elements.overlay.innerHTML = this.renderMenu();
      return;
    }

    if (this.state === "paused") {
      this.elements.overlay.innerHTML = `
        <section class="dialog-card" aria-labelledby="pause-title">
          <div class="eyebrow">TIMEOUT</div>
          <h2 id="pause-title">Game paused</h2>
          <p>${this.currentMode.ranked ? "The ranked run clock keeps moving. No free scouting, champ." : "Your athletes are frozen mid-career."}</p>
          <button class="primary-button" data-action="resume">RESUME</button>
          <button class="secondary-button" data-action="menu">QUIT TO MENU</button>
        </section>
      `;
      return;
    }

    const newBest = this.score > this.bestBeforeRound;
    const activeBoard = this.currentMode.id === "daily" ? this.leaderboards?.daily : this.leaderboards?.global;
    this.elements.overlay.innerHTML = `
      <section class="dialog-card results-card" aria-labelledby="results-title">
        <div class="eyebrow">${escapeHtml(this.currentMode.name)} · FINAL BUZZER</div>
        <h2 id="results-title">${newBest ? "New personal best" : "Run complete"}</h2>
        <div class="final-score"><strong>${this.score}</strong><span>POINTS</span></div>
        <div class="results-best">Personal best <b>${this.best}</b> · Career rep <b>${this.career.xp}</b></div>
        ${this.submissionStatus ? `<div class="submission-status">${escapeHtml(this.submissionStatus)}</div>` : ""}
        ${this.pendingSubmission ? `<button class="secondary-button" data-action="retry-submission">RETRY VERIFIED SUBMISSION</button>` : ""}
        ${activeBoard?.length ? this.renderBoard("TOP RUNS", activeBoard.slice(0, 3)) : ""}
        <button class="primary-button" data-action="restart" ${this.currentMode.id === "daily" ? "disabled" : ""}>${this.currentMode.id === "daily" ? "DAILY ATTEMPT USED" : "RUN IT BACK"}</button>
        <button class="secondary-button" data-action="menu">MAIN MENU</button>
      </section>
    `;
  }

  private renderMenu(): string {
    if (!this.menuExpanded) {
      return `
        <section class="menu-card quick-menu" aria-labelledby="game-title">
          <div class="eyebrow">SHITGATE INTERACTIVE PRESENTS</div>
          <h1 id="game-title"><span>HOOP</span> DREAMS</h1>
          <p class="quick-instruction">DRAG · AIM · RELEASE</p>
          <div class="quick-best"><span>BEST</span><strong>${this.career.bests.career}</strong></div>
          <button class="primary-button quick-play" data-action="quick-start" ${this.starting ? "disabled" : ""}>${this.starting ? "LOADING…" : "PLAY"}</button>
          <button class="secondary-button" data-action="modes">MODES &amp; CAREER</button>
        </section>
      `;
    }

    const tierIndex = tierIndexForXp(this.career.xp);
    const tier = TIERS[tierIndex];
    const nextTier = TIERS[tierIndex + 1];
    const dailyUsed = this.selectedMode === "daily" && this.career.dailyCompleted === utcDay();
    const board = this.selectedMode === "daily" ? this.leaderboards?.daily : this.leaderboards?.global;
    const startLabel = this.starting
      ? "SEEDING THE COURT…"
      : dailyUsed
        ? "DAILY ATTEMPT COMPLETE"
        : this.currentMode.ranked
          ? `START ${this.currentMode.name.toUpperCase()}`
          : "PLAY 60-SECOND RUN";

    return `
      <section class="menu-card menu-card-expanded" aria-labelledby="game-title">
        <header class="menu-title-row">
          <div>
            <div class="eyebrow">MODES &amp; CAREER</div>
            <h1 id="game-title"><span>HOOP</span> DREAMS</h1>
          </div>
          <div class="career-rank"><small>CAREER</small><b>${escapeHtml(tier?.name ?? "Playground")}</b><span>${this.career.xp} REP${nextTier ? ` · ${nextTier.minimumXp - this.career.xp} TO NEXT` : " · MAX TIER"}</span></div>
        </header>
        <div class="mode-tabs" role="tablist" aria-label="Game mode">
          ${Object.values(MODES).map((mode) => `<button class="${mode.id === this.selectedMode ? "selected" : ""}" data-action="mode" data-mode="${mode.id}" ${this.starting ? "disabled" : ""}><small>${mode.eyebrow}</small><b>${mode.name}</b></button>`).join("")}
        </div>
        <div class="menu-dashboard">
          <div class="run-panel">
            <div class="run-description">
              <strong>${escapeHtml(this.currentMode.name)}</strong>
              <span>${escapeHtml(this.currentMode.description)}</span>
            </div>
            <div class="rule-strip">
              <span><b>1–3×</b>GRAB RANGE</span><span><b>${this.currentMode.movingRim ? "MOVING" : "STATIC"}</b>RIM</span><span><b>LIVE</b>WIND</span>
            </div>
            ${this.selectedMode === "career" ? this.renderProfiles(tierIndex) : this.renderRankedSetup()}
            <button class="primary-button" data-action="start" ${this.starting || dailyUsed ? "disabled" : ""}>${startLabel}</button>
            ${this.submissionStatus ? `<div class="submission-status">${escapeHtml(this.submissionStatus)}</div>` : ""}
            <div class="menu-meta"><span><b>${this.best}</b> MODE BEST</span><span>DRAG · AIM · RELEASE</span></div>
          </div>
          <aside class="ranking-panel">
            <label class="field-label">PLAYER NAME<input maxlength="18" data-field="player-name" value="${escapeHtml(this.career.playerName)}" ${this.starting ? "disabled" : ""}></label>
            ${this.currentMode.ranked ? this.renderBoard(this.selectedMode === "daily" ? "TODAY · UTC" : "GLOBAL TOP 5", board?.slice(0, 5) ?? []) : `<div class="career-copy"><b>REP UNLOCKS LINEUPS</b><p>Career builds alter weight, power, size, and perfect-shot tolerance. Ranked modes loan everybody the same Rookie lineup.</p></div>`}
            ${this.leaderboardStatus ? `<div class="board-status">${escapeHtml(this.leaderboardStatus)}</div>` : ""}
          </aside>
        </div>
        <div class="menu-actions">
          <button data-action="simple-menu">Back</button>
          <a href="/store">Store page</a>
        </div>
      </section>
    `;
  }

  private renderProfiles(tierIndex: number): string {
    return `
      <div class="profile-picker" aria-label="Athlete build">
        ${ATHLETE_PROFILES.map((profile) => {
          const locked = profile.unlockTier > tierIndex;
          const selected = profile.id === this.career.selectedProfile;
          return `<button data-action="profile" data-profile="${profile.id}" class="${selected ? "selected" : ""}" ${locked || this.starting ? "disabled" : ""}><b>${locked ? "🔒 " : ""}${profile.name}</b><span>${locked ? TIERS[profile.unlockTier]?.name : profile.tagline}</span></button>`;
        }).join("")}
      </div>
    `;
  }

  private renderRankedSetup(): string {
    return `
      <div class="ranked-setup">
        <div><b>⚖ FIXED ROOKIE LINEUP</b><span>Rankings measure aim, not local career unlocks.</span></div>
        <label class="field-label">CREW CODE<input maxlength="10" data-field="crew-code" placeholder="OPTIONAL" value="${escapeHtml(this.career.crewCode)}" ${this.starting ? "disabled" : ""}></label>
        <button data-action="new-crew" ${this.starting ? "disabled" : ""}>NEW CODE</button>
        <button data-action="copy-crew" ${this.career.crewCode.length < 8 ? "disabled" : ""}>COPY</button>
      </div>
      ${this.career.crewCode.length >= 8 ? this.renderBoard("CREW TOP 5", this.leaderboards?.crew.slice(0, 5) ?? []) : ""}
    `;
  }

  private renderBoard(title: string, entries: LeaderboardEntry[]): string {
    return `
      <div class="leaderboard-mini">
        <h3>${escapeHtml(title)}</h3>
        ${entries.length
          ? `<ol>${entries.map((entry, index) => `<li><i>${index + 1}</i><span>${escapeHtml(entry.name)}</span><b>${entry.score}</b></li>`).join("")}</ol>`
          : `<p>No scores yet. Be the first.</p>`}
      </div>
    `;
  }

  private updateControls(): void {
    this.elements.pauseButton.disabled = this.state === "menu" || this.state === "results" || this.currentMode.ranked;
    this.elements.pauseButton.textContent = this.state === "paused" ? "▶" : "Ⅱ";
    this.elements.pauseButton.setAttribute("aria-label", this.state === "paused" ? "Resume" : "Pause");
    this.elements.soundButton.textContent = this.soundEnabled ? "◖))" : "◖×";
    this.elements.soundButton.setAttribute("aria-label", this.soundEnabled ? "Mute sound" : "Enable sound");
  }

  private toggleSound(): void {
    this.soundEnabled = !this.soundEnabled;
    this.audio.setEnabled(this.soundEnabled);
    writeStoredValue("hoop-dreams-sound", this.soundEnabled ? "on" : "off");
    if (this.soundEnabled) void this.audio.unlock();
    this.updateControls();
    if (this.state === "menu") this.renderOverlay();
  }

  private async toggleFullscreen(): Promise<void> {
    if (document.fullscreenElement) await document.exitFullscreen();
    else await this.elements.shell.requestFullscreen();
  }

  private updateHud(): void {
    this.elements.score.textContent = String(this.score).padStart(2, "0");
    this.elements.time.textContent = String(Math.ceil(this.timeRemaining)).padStart(2, "0");
    this.elements.best.textContent = String(this.best).padStart(2, "0");
    this.elements.streak.textContent = this.streak >= 2 ? `×${this.streak}` : "—";
    this.elements.mode.textContent = this.currentMode.name;
    const direction = Math.abs(this.wind) < 20 ? "CALM" : this.wind > 0 ? "→" : "←";
    this.elements.wind.textContent = `${direction} ${Math.round(Math.abs(this.wind))}`;
    this.elements.time.parentElement?.classList.toggle("urgent", this.timeRemaining <= 10 && this.state === "playing");
  }

  private draw(time: number): void {
    const context = this.context;
    context.save();
    if (this.shake > 0 && !matchMedia("(prefers-reduced-motion: reduce)").matches) {
      context.translate((this.visualRandom.next() - 0.5) * this.shake, (this.visualRandom.next() - 0.5) * this.shake);
    }
    this.drawArena(time);
    this.drawHoop();
    for (const athlete of this.athletes) this.drawAthlete(athlete);
    this.drawEffects();
    if (this.state === "countdown") this.drawCountdown();
    context.restore();
  }

  private drawArena(time: number): void {
    const context = this.context;
    const sky = context.createLinearGradient(0, 0, 0, HEIGHT);
    sky.addColorStop(0, "#111429");
    sky.addColorStop(0.55, "#34243b");
    sky.addColorStop(1, "#f08a47");
    context.fillStyle = sky;
    context.fillRect(0, 0, WIDTH, HEIGHT);

    context.globalAlpha = 0.75;
    for (let index = 0; index < 32; index += 1) {
      const x = (index * 197) % WIDTH;
      const y = 38 + ((index * 71) % 190);
      const pulse = 0.45 + Math.sin(time * 1.4 + index) * 0.2;
      context.fillStyle = `rgba(255, 233, 187, ${pulse})`;
      context.fillRect(x, y, 2, 2);
    }
    context.globalAlpha = 1;

    const moonGlow = context.createRadialGradient(968, 118, 8, 968, 118, 104);
    moonGlow.addColorStop(0, "rgba(255,239,195,.8)");
    moonGlow.addColorStop(1, "rgba(255,239,195,0)");
    context.fillStyle = moonGlow;
    context.beginPath();
    context.arc(968, 118, 104, 0, Math.PI * 2);
    context.fill();
    context.fillStyle = "#ffecc3";
    context.beginPath();
    context.arc(968, 118, 34, 0, Math.PI * 2);
    context.fill();

    context.fillStyle = "#171723";
    const buildings = [
      [0, 360, 135, 170], [123, 318, 110, 212], [219, 388, 146, 142], [350, 295, 128, 235],
      [465, 350, 169, 180], [620, 270, 116, 260], [724, 372, 160, 158], [870, 325, 130, 205],
      [990, 360, 160, 170], [1_135, 305, 145, 225],
    ];
    for (const [x, y, width, height] of buildings) {
      if (x === undefined || y === undefined || width === undefined || height === undefined) continue;
      context.fillRect(x, y, width, height);
      context.fillStyle = "rgba(255,194,98,.38)";
      for (let windowX = x + 18; windowX < x + width - 10; windowX += 28) {
        for (let windowY = y + 20; windowY < y + height - 12; windowY += 34) {
          if ((windowX + windowY) % 3 > 1) context.fillRect(windowX, windowY, 7, 11);
        }
      }
      context.fillStyle = "#171723";
    }

    this.drawCrowd(time);
    const fenceY = 484;
    context.strokeStyle = "rgba(100,110,130,.48)";
    context.lineWidth = 3;
    for (let x = -40; x < WIDTH + 40; x += 28) {
      context.beginPath();
      context.moveTo(x, fenceY);
      context.lineTo(x + 70, GROUND_Y);
      context.stroke();
      context.beginPath();
      context.moveTo(x + 70, fenceY);
      context.lineTo(x, GROUND_Y);
      context.stroke();
    }
    context.fillStyle = "#222939";
    context.fillRect(0, fenceY - 3, WIDTH, 6);

    const court = context.createLinearGradient(0, 505, 0, HEIGHT);
    court.addColorStop(0, "#b75c38");
    court.addColorStop(1, "#6f2f28");
    context.fillStyle = court;
    context.fillRect(0, 505, WIDTH, HEIGHT - 505);
    context.strokeStyle = "rgba(255,229,196,.72)";
    context.lineWidth = 4;
    context.beginPath();
    context.moveTo(0, 646);
    context.lineTo(WIDTH, 646);
    context.stroke();
    context.beginPath();
    context.arc(1_092, 612, 185, Math.PI, Math.PI * 2);
    context.stroke();
    context.strokeRect(936, 505, 312, 141);
    context.fillStyle = "rgba(24,17,24,.2)";
    context.fillRect(0, 654, WIDTH, 66);
    for (let x = 0; x < WIDTH; x += 82) {
      context.fillStyle = x % 164 === 0 ? "rgba(255,255,255,.025)" : "rgba(0,0,0,.025)";
      context.fillRect(x, 505, 82, 215);
    }
  }

  private drawCrowd(time: number): void {
    const context = this.context;
    for (let index = 0; index < 31; index += 1) {
      const x = 18 + index * 42;
      const baseY = 486 - (index % 3) * 4;
      const jump = Math.sin(time * (3.2 + this.crowdEnergy * 4) + index * 1.7) * 7 * this.crowdEnergy;
      context.fillStyle = index % 4 === 0 ? "#282237" : "#1e1b2a";
      context.beginPath();
      context.arc(x, baseY - 39 + jump, 9, 0, Math.PI * 2);
      context.fill();
      context.fillRect(x - 8, baseY - 31 + jump, 16, 28);
      context.strokeStyle = context.fillStyle;
      context.lineWidth = 5;
      context.beginPath();
      context.moveTo(x - 6, baseY - 25 + jump);
      context.lineTo(x - 13, baseY - 41 - this.crowdEnergy * 12 + jump);
      context.moveTo(x + 6, baseY - 25 + jump);
      context.lineTo(x + 13, baseY - 41 - this.crowdEnergy * 12 + jump);
      context.stroke();
    }
  }

  private drawHoop(): void {
    const context = this.context;
    const boardX = this.rimX + 97;
    const supportX = boardX + 28;
    if (this.currentMode.movingRim) {
      context.strokeStyle = "rgba(170,185,201,.35)";
      context.lineWidth = 7;
      context.beginPath();
      context.moveTo(1_000, 91);
      context.lineTo(1_258, 91);
      context.stroke();
      context.fillStyle = "#8692a0";
      context.fillRect(boardX - 8, 82, 16, 18);
    }
    context.strokeStyle = "rgba(0,0,0,.3)";
    context.lineWidth = 15;
    context.beginPath();
    context.moveTo(supportX + 6, 252);
    context.lineTo(supportX + 6, 610);
    context.stroke();
    context.strokeStyle = "#626b78";
    context.lineWidth = 10;
    context.beginPath();
    context.moveTo(supportX, 252);
    context.lineTo(supportX, 610);
    context.stroke();

    const board = context.createLinearGradient(boardX - 10, 0, boardX + 20, 0);
    board.addColorStop(0, "rgba(235,244,250,.7)");
    board.addColorStop(1, "rgba(153,182,199,.45)");
    context.fillStyle = board;
    context.fillRect(boardX - 4, 116, 18, 169);
    context.strokeStyle = "#edf6fa";
    context.lineWidth = 4;
    context.strokeRect(boardX - 6, 114, 22, 173);
    context.strokeStyle = "#f15b3a";
    context.lineWidth = 5;
    context.strokeRect(boardX - 19, 197, 29, 65);

    const left = this.rimX - BASE_RIM.halfWidth;
    const right = this.rimX + BASE_RIM.halfWidth;
    context.strokeStyle = "#626b78";
    context.lineWidth = 8;
    context.beginPath();
    context.moveTo(right, BASE_RIM.y - 2);
    context.lineTo(boardX, BASE_RIM.y - 2);
    context.stroke();
    context.strokeStyle = "rgba(0,0,0,.28)";
    context.lineWidth = 9;
    context.beginPath();
    context.moveTo(left, BASE_RIM.y + 5);
    context.lineTo(right, BASE_RIM.y + 5);
    context.stroke();
    context.strokeStyle = "#f26332";
    context.lineWidth = 8;
    context.lineCap = "round";
    context.beginPath();
    context.moveTo(left, BASE_RIM.y);
    context.lineTo(right, BASE_RIM.y);
    context.stroke();

    context.strokeStyle = "rgba(238,242,239,.78)";
    context.lineWidth = 2;
    for (let index = 0; index <= 8; index += 1) {
      const topX = left + (right - left) * (index / 8);
      const bottomX = this.rimX + (topX - this.rimX) * 0.58;
      context.beginPath();
      context.moveTo(topX, BASE_RIM.y + 3);
      context.quadraticCurveTo(topX, BASE_RIM.y + 45, bottomX, BASE_RIM.y + 78);
      context.stroke();
    }
    for (let row = 1; row <= 3; row += 1) {
      const y = BASE_RIM.y + row * 19;
      const half = 52 - row * 8;
      context.beginPath();
      context.moveTo(this.rimX - half, y);
      context.quadraticCurveTo(this.rimX, y + 7, this.rimX + half, y);
      context.stroke();
    }

    const flagDirection = Math.sign(this.wind) || 1;
    context.strokeStyle = "#c8d0d7";
    context.lineWidth = 3;
    context.beginPath();
    context.moveTo(supportX, 114);
    context.lineTo(supportX, 54);
    context.stroke();
    context.fillStyle = Math.abs(this.wind) > 120 ? "#ff6b4a" : "#f1b44b";
    context.beginPath();
    context.moveTo(supportX, 57);
    context.lineTo(supportX + flagDirection * (26 + Math.abs(this.wind) * 0.08), 66);
    context.lineTo(supportX, 78);
    context.closePath();
    context.fill();
  }

  private drawAthlete(athlete: Athlete): void {
    if (athlete.respawnTimer > 0) return;
    const context = this.context;
    const grounded = !athlete.airborne && !athlete.held;
    const stride = grounded ? Math.sin(athlete.gait) : 0.35;
    const armSwing = grounded ? Math.sin(athlete.gait + Math.PI) * 0.55 : athlete.held ? -1.4 : -0.35;
    const scale = this.activeProfile.size;

    context.save();
    context.globalAlpha = 0.28;
    context.fillStyle = "#120d13";
    context.beginPath();
    const shadowScale = clamp(1 - Math.max(0, GROUND_Y - 36 - athlete.y) / 600, 0.25, 1);
    context.ellipse(athlete.x, GROUND_Y - 4, 27 * shadowScale * scale, 8 * shadowScale * scale, 0, 0, Math.PI * 2);
    context.fill();
    context.restore();

    context.save();
    context.translate(athlete.x, athlete.y);
    context.rotate(athlete.rotation);
    context.scale(athlete.direction * scale, scale);
    context.lineCap = "round";
    context.lineJoin = "round";

    context.strokeStyle = athlete.skinShadow;
    context.lineWidth = 9;
    context.beginPath();
    context.moveTo(-9, 7);
    context.lineTo(-10 - stride * 6, 29);
    context.lineTo(-15 + stride * 5, 39);
    context.moveTo(9, 7);
    context.lineTo(10 + stride * 6, 29);
    context.lineTo(15 - stride * 5, 39);
    context.stroke();

    context.strokeStyle = "#eee9e0";
    context.lineWidth = 8;
    context.beginPath();
    context.moveTo(-15 + stride * 5, 39);
    context.lineTo(-4 + stride * 4, 39);
    context.moveTo(15 - stride * 5, 39);
    context.lineTo(26 - stride * 4, 39);
    context.stroke();
    context.strokeStyle = athlete.jerseyShadow;
    context.lineWidth = 3;
    context.beginPath();
    context.moveTo(-15 + stride * 5, 37);
    context.lineTo(-5 + stride * 4, 37);
    context.moveTo(15 - stride * 5, 37);
    context.lineTo(25 - stride * 4, 37);
    context.stroke();

    context.strokeStyle = athlete.skin;
    context.lineWidth = 9;
    context.beginPath();
    context.moveTo(-15, -15);
    context.lineTo(-23, 1 + armSwing * 5);
    context.lineTo(-18, 14 + armSwing * 8);
    context.moveTo(15, -15);
    context.lineTo(23, 1 - armSwing * 5);
    context.lineTo(20, 14 - armSwing * 8);
    context.stroke();

    const jerseyGradient = context.createLinearGradient(-18, -20, 18, 12);
    jerseyGradient.addColorStop(0, athlete.jersey);
    jerseyGradient.addColorStop(1, athlete.jerseyShadow);
    context.fillStyle = jerseyGradient;
    context.beginPath();
    context.roundRect(-18, -22, 36, 36, [10, 10, 5, 5]);
    context.fill();
    context.strokeStyle = "rgba(255,255,255,.72)";
    context.lineWidth = 2;
    context.beginPath();
    context.moveTo(-12, -20);
    context.quadraticCurveTo(0, -8, 12, -20);
    context.stroke();
    context.fillStyle = "rgba(255,255,255,.92)";
    context.font = "700 13px system-ui";
    context.textAlign = "center";
    context.fillText(String(athlete.number), 0, 3);

    context.fillStyle = athlete.jerseyShadow;
    context.beginPath();
    context.roundRect(-17, 8, 34, 13, 4);
    context.fill();
    context.strokeStyle = "rgba(255,255,255,.55)";
    context.lineWidth = 2;
    context.beginPath();
    context.moveTo(0, 10);
    context.lineTo(0, 20);
    context.stroke();

    const headGradient = context.createRadialGradient(-5, -45, 2, 0, -41, 22);
    headGradient.addColorStop(0, athlete.skin);
    headGradient.addColorStop(1, athlete.skinShadow);
    context.fillStyle = headGradient;
    context.beginPath();
    context.ellipse(0, -42, 19, 22, 0, 0, Math.PI * 2);
    context.fill();
    context.fillStyle = athlete.skinShadow;
    context.beginPath();
    context.ellipse(2, -38, 2.5, 3.5, 0, 0, Math.PI * 2);
    context.fill();

    this.drawHair(athlete);
    const blinking = athlete.blinkTimer < 0;
    context.strokeStyle = "#20150f";
    context.fillStyle = "#f7f0df";
    context.lineWidth = 2;
    if (blinking) {
      context.beginPath();
      context.moveTo(-8, -43);
      context.lineTo(-3, -43);
      context.moveTo(5, -43);
      context.lineTo(10, -43);
      context.stroke();
    } else {
      context.beginPath();
      context.ellipse(-5, -43, 3.4, 2.6, 0, 0, Math.PI * 2);
      context.ellipse(8, -43, 3.4, 2.6, 0, 0, Math.PI * 2);
      context.fill();
      context.fillStyle = "#2a1a13";
      context.beginPath();
      context.arc(-4, -43, 1.4, 0, Math.PI * 2);
      context.arc(9, -43, 1.4, 0, Math.PI * 2);
      context.fill();
    }
    const screaming = athlete.airborne && Math.hypot(athlete.velocityX, athlete.velocityY) > 750;
    context.strokeStyle = "rgba(53,24,14,.8)";
    context.fillStyle = "#2b1712";
    context.lineWidth = 1.8;
    context.beginPath();
    if (screaming) {
      context.ellipse(3, -34, 4, 6, 0, 0, Math.PI * 2);
      context.fill();
    } else {
      context.arc(3, -34, 5, 0.15, Math.PI - 0.15);
      context.stroke();
    }
    context.restore();
  }

  private drawHair(athlete: Athlete): void {
    const context = this.context;
    context.fillStyle = "#171113";
    if (athlete.hair === "fade" || athlete.hair === "buzz") {
      context.beginPath();
      context.ellipse(-1, -58, 18, athlete.hair === "buzz" ? 8 : 11, -0.08, Math.PI, Math.PI * 2);
      context.fill();
      return;
    }
    if (athlete.hair === "high-top") {
      context.beginPath();
      context.roundRect(-16, -73, 31, 21, [7, 7, 3, 3]);
      context.fill();
      return;
    }
    if (athlete.hair === "braids") {
      context.beginPath();
      context.ellipse(-1, -59, 18, 11, 0, Math.PI, Math.PI * 2);
      context.fill();
      context.strokeStyle = "#171113";
      context.lineWidth = 4;
      for (let index = -1; index <= 1; index += 1) {
        context.beginPath();
        context.moveTo(index * 7, -58);
        context.quadraticCurveTo(index * 10, -45, index * 11, -34);
        context.stroke();
      }
      return;
    }
    for (let index = 0; index < 10; index += 1) {
      const angle = (index / 9) * Math.PI;
      context.beginPath();
      context.arc(-16 + index * 3.5, -58 - Math.sin(angle) * 7, 5.5, 0, Math.PI * 2);
      context.fill();
    }
  }

  private drawEffects(): void {
    const context = this.context;
    for (const particle of this.particles) {
      context.globalAlpha = clamp(particle.life / particle.maximumLife, 0, 1);
      context.fillStyle = particle.color;
      context.beginPath();
      context.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
      context.fill();
    }
    context.textAlign = "center";
    context.font = "900 22px system-ui";
    for (const text of this.floatingTexts) {
      context.globalAlpha = clamp(text.life / 0.35, 0, 1);
      context.fillStyle = text.color;
      context.shadowColor = "rgba(0,0,0,.7)";
      context.shadowBlur = 8;
      context.fillText(text.text, text.x, text.y);
    }
    context.shadowBlur = 0;
    context.globalAlpha = 1;
  }

  private drawCountdown(): void {
    const context = this.context;
    const value = Math.max(1, Math.ceil(this.countdownRemaining));
    const pulse = 1 + (this.countdownRemaining % 1) * 0.22;
    context.fillStyle = "rgba(6,7,14,.28)";
    context.fillRect(0, 0, WIDTH, HEIGHT);
    context.save();
    context.translate(WIDTH / 2, HEIGHT / 2);
    context.scale(pulse, pulse);
    context.fillStyle = "#fff";
    context.shadowColor = "rgba(236,125,39,.85)";
    context.shadowBlur = 32;
    context.font = "900 112px system-ui";
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText(String(value), 0, 0);
    context.restore();
  }

  private spawnScoreBurst(x: number, y: number, perfect: boolean): void {
    const colors = perfect ? ["#ffe274", "#ffffff", "#f08a47"] : ["#ffffff", "#69dbff", "#f08a47"];
    for (let index = 0; index < 30; index += 1) {
      const angle = this.visualRandom.next() * Math.PI * 2;
      const speed = 80 + this.visualRandom.next() * 260;
      this.particles.push({
        x,
        y,
        velocityX: Math.cos(angle) * speed,
        velocityY: Math.sin(angle) * speed - 130,
        life: 0.55 + this.visualRandom.next() * 0.5,
        maximumLife: 1.05,
        color: colors[index % colors.length] ?? "#fff",
        radius: 2 + this.visualRandom.next() * 4,
      });
    }
  }

  private spawnDust(x: number, y: number, count: number): void {
    for (let index = 0; index < count; index += 1) {
      this.particles.push({
        x: x + (this.visualRandom.next() - 0.5) * 24,
        y,
        velocityX: (this.visualRandom.next() - 0.5) * 60,
        velocityY: -20 - this.visualRandom.next() * 35,
        life: 0.22 + this.visualRandom.next() * 0.2,
        maximumLife: 0.42,
        color: "#d28a64",
        radius: 3 + this.visualRandom.next() * 4,
      });
    }
  }

  private createAthlete(index: number): Athlete {
    const skin = SKINS[index % SKINS.length] ?? SKINS[0];
    const jersey = JERSEYS[index % JERSEYS.length] ?? JERSEYS[0];
    const direction = index % 2 === 0 ? 1 : -1;
    const x = WALK_ZONE_MIN_X + 35 + index * 103 + this.gameplayRandom.next() * 18;
    return {
      id: index,
      x,
      y: GROUND_Y - 36,
      previousX: x,
      previousY: GROUND_Y - 36,
      velocityX: direction * (40 + this.gameplayRandom.next() * 35),
      velocityY: 0,
      direction,
      airborne: false,
      held: false,
      touched: false,
      scored: false,
      rotation: 0,
      angularVelocity: 0,
      gait: this.gameplayRandom.next() * Math.PI * 2,
      turnTimer: 1 + this.gameplayRandom.next() * 4,
      respawnTimer: 0,
      collisions: 0,
      throwPoints: 1,
      throwOriginTick: 0,
      throwOriginX: x,
      skin: skin[0],
      skinShadow: skin[1],
      jersey: jersey[0],
      jerseyShadow: jersey[1],
      number: 4 + index * 3,
      hair: HAIR[index % HAIR.length] ?? "fade",
      blinkTimer: 0.6 + this.visualRandom.next() * 4,
    };
  }

  private resetAthlete(athlete: Athlete): void {
    const random = this.athleteRandoms[athlete.id] ?? this.gameplayRandom;
    athlete.x = WALK_ZONE_MIN_X + 20 + athlete.id * 108 + random.next() * 24;
    athlete.y = GROUND_Y - 36;
    athlete.previousX = athlete.x;
    athlete.previousY = athlete.y;
    athlete.direction = random.next() < 0.5 ? -1 : 1;
    athlete.velocityX = athlete.direction * (40 + random.next() * 35);
    athlete.velocityY = 0;
    athlete.airborne = false;
    athlete.held = false;
    athlete.touched = false;
    athlete.scored = false;
    athlete.rotation = 0;
    athlete.angularVelocity = 0;
    athlete.turnTimer = 1 + random.next() * 4;
    athlete.respawnTimer = 0;
    athlete.collisions = 0;
    athlete.throwPoints = 1;
    athlete.throwOriginTick = this.roundTick;
    athlete.throwOriginX = athlete.x;
  }

  private resolveSelectedProfile(): AthleteProfile {
    const profile = profileById(this.career.selectedProfile);
    if (profile.unlockTier <= tierIndexForXp(this.career.xp)) return profile;
    this.career.selectedProfile = "rookie";
    return profileById("rookie");
  }

  private saveCareer(): void {
    writeStoredValue(CAREER_KEY, JSON.stringify(this.career));
  }

  private savePendingSubmission(): void {
    if (this.pendingSubmission) writeStoredValue(PENDING_SUBMISSION_KEY, JSON.stringify(this.pendingSubmission));
  }

  private clearPendingSubmission(): void {
    this.pendingSubmission = null;
    removeStoredValue(PENDING_SUBMISSION_KEY);
  }

  private toWorldPoint(event: PointerEvent): { x: number; y: number } {
    const bounds = this.elements.canvas.getBoundingClientRect();
    return {
      x: clamp((event.clientX - bounds.left) * (WIDTH / bounds.width), 0, WIDTH),
      y: clamp((event.clientY - bounds.top) * (HEIGHT / bounds.height), 0, HEIGHT),
    };
  }

}

function parsePendingSubmission(value: string | null): PendingRankedSubmission | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as Partial<PendingRankedSubmission>;
    const run = parsed.run;
    if (
      !run || !/^[a-f0-9]{32}$/.test(run.id) ||
      (run.mode !== "moving" && run.mode !== "daily") ||
      !Number.isInteger(run.seed) || typeof run.day !== "string" ||
      typeof run.crewCode !== "string" || !Number.isFinite(run.expiresAt) ||
      !Number.isFinite(run.validAfter) || run.rulesVersion !== RULES_VERSION ||
      typeof parsed.playerName !== "string"
    ) return null;
    return {
      run: run as RankedRun,
      playerName: parsed.playerName.slice(0, 18),
      trace: parseRankedTrace(parsed.trace),
    };
  } catch {
    return null;
  }
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  })[character] ?? character);
}

function messageFrom(error: unknown): string {
  return error instanceof Error ? error.message : "Leaderboard unavailable";
}
